import React, { createContext, ReactNode, useContext } from 'react';
import {
    ODataQuery,
    useListContext,
    UseListContextReturn,
} from '../../contextsProperty/useListContext';
import {
    defaultFilterTypes,
    IFilterOptionState,
    sortDirections,
} from '../../../types/lists/listTypes';
import { readOnlyEntities } from '../../../utils/commonVariables/entityKeys';
import { ISupplierHotel } from '../../../types/entities/ISupplierHotel';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface IHotelListItem {
    Id: string;
    Name: string;
    IdHotel: number;
    LastChange: Date | string;
    IdZitoneoChange: number;
    ZitoneoPublishStatus: string;
    ZitoneoPublishStatusKey: string;
    ZitoneoPublishStatusColor: string;
}

export interface IHotelListFilters {
    name: {
        filterType: string;
        filterKey: string;
        value: string;
    };
    status: {
        filterType: string;
        filterKey: string;
        value: IFilterOptionState | null;
    };
}

type HotelsListContextType = UseListContextReturn<
    IHotelListItem,
    IHotelListFilters
>;

// ─── Context ──────────────────────────────────────────────────────────────────

const ContextHotelsList = createContext<HotelsListContextType | undefined>(
    undefined
);

// ─── Provider ─────────────────────────────────────────────────────────────────

const INITIAL_FILTERS: IHotelListFilters = {
    name: {
        filterType: defaultFilterTypes.DEFAULT_STRING_FILTER_TYPE,
        filterKey: 'Name',
        value: '',
    },
    status: {
        filterType: defaultFilterTypes.DEFAULT_INTEGER_ID_FILTER_TYPE,
        filterKey: 'Hotel/ZitoneoPublishStatus/IdZitoneoPublishStatusType',
        value: null,
    },
};

const ITEMS_PER_PAGE = 100;

export const ProviderHotelsList = ({ children }: { children: ReactNode }) => {
    const listContext = useListContext<
        ISupplierHotel,
        IHotelListItem,
        IHotelListFilters
    >({
        initialFilters: INITIAL_FILTERS,
        buildQuery: (filtersToOdata, sortedBy, sortDir): ODataQuery => ({
            entityName: readOnlyEntities.SupplierHotel,
            query: {
                select: [
                    'Id',
                    'Name',
                    'IdHotel',
                    'Hotel/HotelZitoneoChange/ChangeDate',
                    'Hotel/IdHotelZitoneoChange',
                    'Hotel/ZitoneoPublishStatus/ZitoneoPublishStatusType/LocalizedCaption',
                    'Hotel/ZitoneoPublishStatus/ZitoneoPublishStatusType/Caption',
                    'Hotel/ZitoneoPublishStatus/ZitoneoPublishStatusType/Name',
                    'Hotel/ZitoneoPublishStatus/ZitoneoPublishStatusType/Key',
                    'Hotel/ZitoneoPublishStatus/ZitoneoPublishStatusType/Color',
                ],
                filter: [...filtersToOdata, `Hotel/ZitoneoMembership`],
                orderBy: `${sortedBy} ${sortDir}`,
                expand: [
                    'Hotel',
                    'Hotel/HotelZitoneoChange',
                    'Hotel/ZitoneoPublishStatus',
                    'Hotel/ZitoneoPublishStatus/ZitoneoPublishStatusType',
                ],
                top: ITEMS_PER_PAGE,
            },
        }),
        adaptItem: (raw): IHotelListItem => ({
            Id: raw.Id,
            Name: raw.Name,
            IdHotel: raw.IdHotel,
            LastChange: raw.Hotel?.HotelZitoneoChange?.ChangeDate,
            IdZitoneoChange: raw.Hotel?.IdHotelZitoneoChange || 0,
            ZitoneoPublishStatus:
                raw.Hotel?.ZitoneoPublishStatus?.ZitoneoPublishStatusType
                    ?.LocalizedCaption ||
                raw.Hotel?.ZitoneoPublishStatus?.ZitoneoPublishStatusType
                    ?.Caption ||
                raw.Hotel?.ZitoneoPublishStatus?.ZitoneoPublishStatusType
                    ?.Name ||
                '',
            ZitoneoPublishStatusKey:
                raw.Hotel?.ZitoneoPublishStatus?.ZitoneoPublishStatusType
                    ?.Key || '',
            ZitoneoPublishStatusColor:
                raw.Hotel?.ZitoneoPublishStatus?.ZitoneoPublishStatusType
                    ?.Color || '',
        }),
        itemsPerPage: ITEMS_PER_PAGE,
        initialSortBy: 'Name',
        initialSortDir: sortDirections.ASC,
    });

    return (
        <ContextHotelsList.Provider value={listContext}>
            {children}
        </ContextHotelsList.Provider>
    );
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useHotelsList = () => {
    const context = useContext(ContextHotelsList);

    if (!context) {
        throw new Error(
            'useHotelsList must be used within a ProviderHotelsList'
        );
    }

    return context;
};
